import React, { useEffect, useRef, useState, useMemo } from "react";
import styled from "styled-components";
import { Theme, Spacer, Icon, Title } from "@dspworkplace/ui";
import { NavImage, MobileNavImage } from "../../Image/logo";
import { NavLink, useLocation, matchPath, useHistory } from "react-router-dom";
import { useAuth, getCompanyId } from "../../../Auth";
import { SummaryButton } from "../../../pages/Summary";
import IsGranted from "../../../security/IsGranted";
import AccessCheck from "../../../security/AccessCheck";
import { useReleaseNote } from "../../../Utilities/Headwayapp";
import Modal, { useModal } from "../../../components/Modal";
import { getCookie, getEnableAutomatedScorecardCoachingMetric, setCookieEndOfDay } from "../../../Utilities/index.ts";
import Tooltip from "../../Tooltip";
import { useDeviceType } from "../../../hooks/useDeviceType.js";
import { useReportIssue } from "../../../pages/Fleet/Overview/component/ReportIssueContext.js";
import StartInspection from "../../Inspection/StartInspection";
import ReportIssuePage from "../../../pages/Fleet/Overview/reportIssue/index.js";

const navSize = "56px";

const Nav = styled.nav`
    background: #EAF0F8;
    height: ${navSize};
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #e2e8f0;
    position: sticky;
    top: 0;
    z-index: 13;
`;

const MobileNav = styled.nav`
    background: ${Theme.colors.neutrals.white};
    height: 56px;
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    border-bottom: ${({ visible }) => (visible ? "none" : "1px solid #e2e8f0")};
    position: ${({ isPhonebook }) => (isPhonebook ? "fixed" : "sticky")};
    top: 0;
    z-index: 9999;
    box-shadow: ${({ visible }) => (visible ? "none" : "0 1px 3px 0 #0000001a, 0 1px 2px -1px #0000001a")};
`;

const SubNav = styled(Nav)`
    background: ${Theme.colors.neutrals.white};
    justify-content: flex-start;
    height: 50px;
    top: ${navSize} !important;
    z-index: 12;

    li { 
    line-height: normal;
    display: inline-flex;
    align-items: center;
    height: 100%; 
    }

    li a { color: #95A1B8 !important; }
    li a:hover { color: #374151 !important; }
    li.active a { color: #00538A !important; font-weight: 500; }
`;

const SpacerBorder = styled(Spacer)`
    width: ${(props) => props.width};
    display: inline-flex;
    align-items: center;
    height: 100%;
`;

const Menu = styled(Spacer)`
    flex: 1;
    align-items: center;
    height: ${navSize};
    flex-grow: 2;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: thin;
`;

export const Tabs = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    height: 100%;
    white-space: nowrap;

    > li {
        display: inline-block;
        height: 100%;
        line-height: ${navSize};
        padding: 0 16px;

        &.active {
            position: relative;

            &:after {
                content: "";
                width: 70%;
                height: 3px;
                background: #0C639D;
                border-radius: 3px;
                position: absolute;
                bottom: 0;
                left: 50%;
                transform: translateX(-50%);
            }

            a {
                font-weight: 600;
                color: #00538A;
            }
        }

        a {
            font-size: 14px;
            color: #65748D;
            font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
            font-weight: 400;
            text-align: center;
            transition: color 0.15s;

            &:hover {
                color: #374151;
            }
        }

        svg {
            vertical-align: middle;
        }

        @media (max-width: 1200px) {
            padding-left: 8px;
            padding-right: 8px;
        }
    }
`;

const SpacerInline = styled(Spacer)`
    display: inline-block;
`;

const UserName = styled.p`
    font-size: 14px;
    font-weight: 500;
    color: #0f172a;
    margin: 0;
    line-height: 1.3;
`;

const JobTitle = styled.p`
    font-size: 12px;
    color: #64748b;
    margin: 0;
    line-height: 1.3;
`;

export const MenuItemBadge = styled.sup`
    line-height: 1;
    align-self: baseline;
    color: ${Theme.colors.primary};
    font-weight: bold;
    text-indent: 4px;
    display: inline-block;
`;

const ProfileContainer = styled.div`
    cursor: pointer;
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ProfileOptions = styled.div`
    position: absolute;
    top: 48px;
    right: 0;
    background: #ffffff 0% 0% no-repeat padding-box;
    box-shadow: 0px 0px 6px #cccccc;
    border: 1px solid #cccccc;
    border-top: none;
    border-radius: 0 0 2px 2px;
    z-index: 5000;
    min-width: 120px;
`;

const Option = styled.div`
    padding: 8px;
    color: #707070;
    font-size: 12px;
    text-align: center;

    > a {
        padding: 8px;
        color: #707070;
        font-size: 12px;
        text-align: center;
    }

    &.version {
        font-size: 8px;
        font-weight: bold;
    }

    &:hover {
        background: #eaf0f6;
    }
`;

const IconBtn = styled.button.attrs({ className: 'nav-icon-btn' })`
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;
    position: relative;
    color: #64748b;

    &&& svg, &&& svg path, &&& svg circle, &&& svg rect, &&& * svg {
        fill: none !important;
    }

    &:hover {
        background: #f1f5f9;
    }
`;

const ProfileDivider = styled.div`
    width: 1px;
    height: 32px;
    background: #e2e8f0;
    margin: 0 8px;
`;

const OptionMobile = styled.div`
    padding: 8px;
    color: #62748e;
    display: flex;
    align-items: center;

    svg path {
        fill: #62748e;
        transition: fill 150ms ease;
    }

    &:hover {
        svg path {
            fill: #00538a;
        }
    }

    .timesV2-icon path {
        stroke: #62748e;
    }

    &:hover {
        .timesV2-icon path {
            stroke: #00538a;
        }
    }

`;

const DrawerWrapper = styled.div`
    height: 100%;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`

const DrawerSection = styled.div`
    padding: 0 16px;

    h4 {
        font-size: 12px;
        color: #94a3b8;
        margin-bottom: 8px;
        text-transform: uppercase;
    }

    a {
        display: block;
        padding: 10px 12px;
        border-radius: 10px;
        color: #1f2937;
        text-decoration: none;
        margin-bottom: 6px;

        &.active {
            background: #eef5ff;
            color: #2563eb;
        }
    }
`;

const DrawerFooter = styled.div`
    position: sticky;
    bottom: 0;
    padding: 16px 16px 24px 16px;
    background: #fff;
    border-top: 1px solid #f9f3f4;
    z-index: 9999;
`;

const DrawerItem = styled.div`
    font-size: 12px !important;
    color: #90a1b9 !important;
    margin-bottom: 8px !important;
    text-transform: uppercase;
    display: flex !important;
    justify-content: space-between;
    align-items: center;
    font-weight: bold !important;
    padding: 0 8px !important;
    line-height: 1.33333;

    &.active {
        background: unset !important;
    }
`;

const DrawerSubItem = styled(NavLink)`
    display: flex !important;
    align-items: center;
    gap: 12px;
    height: 48px;
    font-size: ${Theme.font.medium.size};
    font-family: ${Theme.font.main};
    border-radius: 16px !important;
    color: #475569 !important;
    font-weight: 500;
    padding: 12px !important;
    margin-bottom: 4px !important;

    &.active,
    &:hover {
        background: #EAF0F6 !important;
        color: #00538A !important;
    }

    &.active {
        border: 1px solid;
        border-color: color-mix(in oklab, #00538A 20%, transparent);
    }
`;

const SecondaryAction = styled.div`
    cursor: pointer;
    height: 50px;
    background: ${({ active }) => (active ? "#eaf0f6" : "transparent")};
    border-radius: 15px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
    border-color: ${({ active }) => (active ? "color-mix(in oklab,#00538a 20%,transparent)" : "#314158")};
    font-size: ${Theme.font.medium.size};
    font-family: ${Theme.font.main};
    color: #475569 !important;
    font-weight: 500;
    padding: 12px !important;
    margin-bottom: 4px !important;
    height: 48px;
    border: ${({ active }) => active ? "1px solid #c2d1e0" : "none"};

    &.active,
    &:hover {
        background: #EAF0F6 !important;
        color: #00538A !important;
    }

    &.active {
        border: 1px solid;
        border-color: color-mix(in oklab, #00538A 20%, transparent);
    }
`;

const ReportButton = styled.button`
    color: #314158;
    font-weight: 500;
    font-size: ${Theme.font.medium.size};
    font-family: ${Theme.font.main};
    border: 1px solid #e2e8f0;
    padding: 12px 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border-radius: 16px;
    cursor: pointer;
    height: 48px;
    width: 100%;

    &:hover, &:focus {
        background-color: #e2e8f0;
        outline: none;
    }

    svg {
        margin-bottom: 1.5px;
    }
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const ComingSoonBadge = styled.span`
    font-size: 10px;
    background: #f1f5f9;
    color: #64748b;
    padding: 2px 6px;
    border-radius: 10px;
    margin-left: auto;
    font-weight: 700;
`;

const DisabledItem = styled.div`
    display: flex !important;
    align-items: center;
    gap: 12px;
    height: 48px;
    font-size: ${Theme.font.medium.size};
    font-family: ${Theme.font.main};
    border-radius: 16px !important;
    color: #475569 !important;
    font-weight: 500;
    padding: 12px !important;
    margin-bottom: 4px !important;
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
    background: none !important;
    border: none !important;
`;

const getAllMobileRoutes = (routes) => {
    const fleetRoute = routes.find((r) => r.path === "/fleet");

    if (!fleetRoute) return [];

    const filterChildren = (subRoutes, parentPath = "") => {
        return subRoutes
            .map((route) => {
                const isMobileRoute = route.view === "mobile" || route.view === "both";

                if (route.useMenu === false) return null;
                let children = [];
                if (route.routes) {
                    children = filterChildren(route.routes, `${parentPath}${route.path}`);
                }

                if (!isMobileRoute && children.length === 0) {
                    return null;
                }

                return {
                    name: route.name === "Overview" || route.name === "Management" ? `Fleet ${route.name}` : route.name,
                    path: route.path,
                    ...(route.icon && { icon: route.icon }),
                    ...(route.component && { component: route.component }),
                    ...(route.feature && { feature: route.feature }),
                    ...(route.avoidPermission && { avoidPermission: route.avoidPermission }),
                    ...(children.length && { routes: children }),
                };
            })
            .filter(Boolean);
    };

    return [
        {
            name: fleetRoute.name,
            path: fleetRoute.path,
            routes: filterChildren(fleetRoute.routes || [], fleetRoute.path),
        },
    ];
};

const App = ({ routes, children }) => {
    let { pathname } = useLocation();
    const { isMobile } = useDeviceType();
    const { setShowReportModal, closeReportModal, showReportModal, onSuccessCallback } = useReportIssue();
    const mobileRoutes = getAllMobileRoutes(routes);

    const activeRoute = useMemo(() => {
        return routes.find((route) => matchPath(pathname, route.path));
    }, [routes, pathname]);

    const handleReportIssueSuccess = (data) => {
        closeReportModal()
        onSuccessCallback?.(data);
    }

    return (
        <>
            {isMobile ? (
                <NavSectionMobile routes={mobileRoutes} isMobile={isMobile} setShowReportModal={setShowReportModal} closeReportModal={closeReportModal} />
            ) : (
                <NavSectionDesktop routes={routes} activeRoute={activeRoute} children={children} />
            )}
            {showReportModal &&
                <ReportIssuePage
                    cancel={() => closeReportModal()}
                    success={handleReportIssueSuccess}
                />
            }
        </>
    );
};

const NavSubMenuDesktop = ({ mainRoute, pathname }) => {

    useEffect(() => {
        if (pathname.startsWith("/fleet")) {
            setCookieEndOfDay("fleet_last_visited_route", pathname);
        }
    }, [pathname]);

    if (mainRoute.renderMenu === false) return null;
    if (
        !mainRoute.routes ||
        mainRoute.routes.filter((r) => r.renderMenu === true || r.renderMenu === undefined).length === 0
    ) {
        return false;
    }

    const shouldHideSubMenu =
        mainRoute.path === "/fleet" &&
        mainRoute.routes.some(
            (route) =>
                route.path === "/report-issue" &&
                route.renderMenu === false &&
                matchPath(pathname, "/fleet/report-issue")
        );

    if (shouldHideSubMenu) {
        return null;
    }

    return (
        <SubNav className="sub-nav-main">
            <SpacerBorder width="10px" />
            <Tabs>
                {mainRoute.routes.map((route, i) => {
                    if (
                        getEnableAutomatedScorecardCoachingMetric() === "false" &&
                        route.path === "/forced-coaching-metric"
                    )
                        return null;
                    if (route.renderMenu === false) return null;

                    let activeClass = "";
                    let result = matchPath(pathname, mainRoute.path + route.path);
                    if (result) activeClass = "active";

                    let beta = null;
                    if (route.isCompany)
                        if (route.isCompany.includes(Number.parseInt(getCompanyId(), 10))) { beta = <MenuItemBadge>Beta</MenuItemBadge>; }
                        else { return null; }

                    return (
                        <li key={i} className={activeClass}>
                            <NavLink
                                to={`${mainRoute.path}${route.menuPath ?? route.path}`}
                            // onClick={() => handleSubMenuNavClick(route)}
                            >
                                {route.name}
                                {beta}
                            </NavLink>
                        </li>
                    );
                })}
            </Tabs>
        </SubNav>
    );
};

const isRouteActive = (pathname, route) => {
    if (route.matchPath) {
        return matchPath(pathname, route.matchPath);
    }
    return matchPath(pathname, route.path);
};

const getFleetRoute = () => {
    const lastRoute = getCookie("fleet_last_visited_route");

    if (lastRoute && lastRoute.startsWith("/fleet")) {
        return lastRoute;
    }

    return "/fleet/overview";
};

const NavSectionDesktop = ({ routes, activeRoute, children }) => {
    const { pathname } = useLocation();
    const re = useReleaseNote();

    const fleetRoute = useMemo(() => {
        const lastRoute = getFleetRoute();
        return lastRoute;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    const isLoadout = pathname === "/scheduler/loadout";

    useEffect(() => {
        const navMain = document.querySelector(".nav-main");
        if (!navMain) return;
        navMain.classList.toggle("loadout-nav", isLoadout);
        return () => {
            navMain.classList.remove("loadout-nav");
        };
    }, [isLoadout]);
    return (
        <>
            <Nav className={"nav-main"}>
                <SpacerBorder left={5} right={4} width="215px" style={{ paddingLeft: 30 }}>
                    <NavLink to="/dashboard">
                        <NavImage />
                    </NavLink>
                </SpacerBorder>
                <Menu style={{ flexGrow: 2, scrollbarWidth: "none" }}>
                    <Tabs>
                        {routes.map((route, i) => {
                            if (route.renderMenu === false || route.useMenu === false) return null;
                            if (route?.isCompany && !route.isCompany.includes(Number.parseInt(getCompanyId()))) return null;

                            return (
                                <li key={i} className={matchPath(pathname, route.path) ? "active" : ""}>
                                    <NavLink to={route.name === "Fleet" ? fleetRoute : (route.component ? route.path : `${route.path}${route.routes[0].menuPath ?? route.routes[0].path}`)}>
                                        {route.name}
                                    </NavLink>
                                </li>
                            );
                        })}
                    </Tabs>
                </Menu>
                <div style={{ display: "flex", alignItems: "center", gap: 12, paddingRight: 16 }}>
                    {localStorage.getItem("currentUserRole") !== "ROLE_CHAT_USER" && (
                        <>
                            {children}
                            <IsGranted feature={AccessCheck.features.STATION_NARRATIVE}>
                                <div id="summary-button" style={{ display: "flex", alignItems: "center" }}>
                                    <SummaryButton />
                                </div>
                            </IsGranted>
                            <IsGranted feature={AccessCheck.features.SETTINGS}>
                                <NavLink to="/settings" style={{ display: "flex", alignItems: "center" }}>
                                    <IconBtn
                                        title="Settings"
                                        style={matchPath(pathname, "/settings") ? { background: "rgba(0,83,138,0.08)" } : {}}
                                    >
                                        <Icon.MaintenanceV2 size="20px" />
                                    </IconBtn>
                                </NavLink>
                            </IsGranted>
                            {re && <div style={{ display: "flex", alignItems: "center" }}>{re}</div>}
                        </>
                    )}
                    <ProfileDivider />
                    <Profile />
                </div>
            </Nav>
            {activeRoute && <NavSubMenuDesktop mainRoute={activeRoute} pathname={pathname} />}
        </>
    );
};

const Profile = () => {
    const popupRef = useRef();
    const [open, setOpen] = useState(false);
    const { logout } = useAuth();

    useEffect(() => {
        document.addEventListener("mouseup", handleClick, false);
        return () => document.removeEventListener("mouseup", handleClick, false);
    }, []);

    const handleClick = (e) => {
        if (popupRef.current && !popupRef.current.contains(e.target)) {
            setOpen(false);
        } else {
            setOpen(true);
        }
    };

    const name = localStorage.getItem("friendlyName") || "First Last";
    const title = localStorage.getItem("title") || "Driver";
    const shortTitle = title.length > 23
        ? <Tooltip content={title} verticalPosition="bottom">{title.substring(0, 22) + "..."}</Tooltip>
        : title;

    return (
        <ProfileContainer ref={popupRef} id="profile-container">
            <div style={{ textAlign: "right" }}>
                <UserName>{name}</UserName>
                <JobTitle>{shortTitle}</JobTitle>
            </div>
            {open ? <Icon.ArrowUp size='16px' color='#94a3b8' /> :
                <Icon.ArrowDown size='16px' color='#94a3b8' />}
            {open && (
                <ProfileOptions>
                    <Option>
                        <NavLink to="/edit-profile">Edit Profile</NavLink>
                    </Option>
                    <Option onClick={() => logout().then()}>
                        Logout
                    </Option>
                </ProfileOptions>
            )}
        </ProfileContainer>
    );
};

const NavSectionMobile = ({ routes, isMobile, setShowReportModal, closeReportModal }) => {
    const [shouldRender, setShouldRender] = useState(false);
    const [visible, setVisible] = useModal(false);
    let { pathname } = useLocation();
    let history = useHistory();
    const isPhonebook = pathname === "/phonebook";

    useEffect(() => {
        closeReportModal()
    }, [pathname, closeReportModal]);

    useEffect(() => {
        if (!isMobile && visible) {
            setVisible(false);
        }
    }, [isMobile, setVisible, visible]);

    useEffect(() => {
        if (visible) {
            setShouldRender(true);
        } else {
            const t = setTimeout(() => setShouldRender(false), 300);
            return () => clearTimeout(t);
        }
    }, [visible]);

    const handleReportBtnClick = () => {
        setVisible(false);
        setShowReportModal(true);
    }

    return (
        <MobileNav className={"nav-main"} visible={visible} isPhonebook={isPhonebook}>
            <Spacer left={4}>
                <NavLink to="/fleet/overview" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <MobileNavImage />
                    <Title
                        style={{ lineHeight: "unset", fontSize: "18px", color: "#00538A", letterSpacing: "-0.45px" }}
                    >
                        Fleet Manager
                    </Title>
                </NavLink>
            </Spacer>
            <SpacerInline right={3}>
                <div style={{ display: "flex" }} id="mobile-actions-container">
                    <OptionMobile>
                        <Icon.Persons size="22px" onClick={() => { closeReportModal(); history.push("/phonebook") }} />
                    </OptionMobile>
                    <OptionMobile>
                        {visible ? (
                            <Icon.TimesV2
                                size="24px"
                                color="none"
                                className="timesV2-icon"
                                onClick={() => {
                                    setVisible(false);
                                }}
                                style={{ cursor: "pointer", width: "26px" }}
                            />
                        ) : (
                            <Icon.Menu
                                size="26px"
                                onClick={() => {
                                    setVisible(true);
                                }}
                                style={{ cursor: "pointer" }}
                            />
                        )}
                    </OptionMobile>
                    {shouldRender && (
                        <Modal
                            width={"280px"}
                            drawer={"right"}
                            visible={true}
                            setVisible={setVisible}
                            removeHeader={true}
                            closeOnOverlay={true}
                            paddingTopBottomZero={true}
                            modalStyle={{
                                backgroundColor: "#fafbfc",
                                padding: "0",
                                paddingBottom: "0",
                                animation: visible ? "slideInRight 0.3s ease-out forwards" : "slideOutRight 0.3s ease-in forwards",
                            }}
                            overlayStyle={{
                                width: '100%',
                                backgroundColor: "#0f172b33",
                                animation: visible ? "fadeIn 0.3s ease-out forwards" : "fadeOut 0.3s ease-in forwards",
                                zIndex: 9998,
                                backdropFilter: "blur(8px)",
                            }}
                            modalContentStyle={{
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <DrawerWrapper>
                                <div>
                                    <div
                                        style={{
                                            height: "57px",
                                        }}
                                    ></div>
                                    <DrawerSection style={{ paddingTop: "16px" }}>
                                        {routes.map((route, i) => {
                                            if (route.useMenu === false || route.renderMenu === false) return null;

                                            const mainActive = isRouteActive(pathname, route);

                                            return (
                                                <div key={i} style={{ marginBottom: "16px", borderBottom: "1px solid #e2e8f0", paddingBottom: "12px" }}>
                                                    <Row>
                                                        <DrawerItem
                                                            to={
                                                                route.component
                                                                    ? route.path
                                                                    : route.routes?.[0]
                                                                        ? route.path + route.routes[0].path
                                                                        : route.path
                                                            }
                                                            className={mainActive ? "active" : ""}
                                                        >
                                                            {route.name}
                                                        </DrawerItem>
                                                    </Row>
                                                    {(!route.routes ||
                                                        route.routes.some(
                                                            (r) => r.renderMenu === true || r.renderMenu === undefined
                                                        )) &&
                                                        route.routes?.map((sub, j) => {
                                                            const subPath = route.path + sub.path;
                                                            const subActive = matchPath(pathname, subPath);
                                                            const subKey = `${route.path}${sub.path}`;

                                                            const SubIcon = sub.icon ? Icon[sub.icon] : Icon["Truck"];

                                                            return (
                                                                <div key={subKey}>
                                                                    {sub.isComingSoon ? (
                                                                        <DisabledItem>
                                                                            {SubIcon && (
                                                                                <SubIcon
                                                                                    size="20px"
                                                                                    color="currentColor"
                                                                                />
                                                                            )}
                                                                            {sub.name}
                                                                            <ComingSoonBadge>Coming Soon</ComingSoonBadge>
                                                                        </DisabledItem>
                                                                    ) : (
                                                                        <DrawerSubItem
                                                                            to={subPath}
                                                                            className={subActive ? "active" : ""}
                                                                            onClick={() => setVisible(false)}
                                                                        >
                                                                            {SubIcon && (
                                                                                <SubIcon
                                                                                    size="20px"
                                                                                    color="currentColor"
                                                                                />
                                                                            )}
                                                                            {sub.name}
                                                                        </DrawerSubItem>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            );
                                        })}
                                    </DrawerSection>
                                    <DrawerSection>
                                        <div style={{ marginBottom: "16px", borderBottom: "1px solid #e2e8f0", paddingBottom: "12px" }}>
                                            <DrawerItem to="/scheduler/week">Scheduler</DrawerItem>
                                            <DrawerSubItem to="/scheduler/week"
                                                onClick={() => setVisible(false)}
                                                active={matchPath(pathname, "/scheduler/week") ? 1 : 0}
                                            >
                                                <Icon.CalendarV2 color="none" strokeColor="currentColor" size="20px" />
                                                <span>Week</span>
                                            </DrawerSubItem>
                                            <DrawerSubItem to="/scheduler/day"
                                                onClick={() => setVisible(false)}
                                                active={matchPath(pathname, "/scheduler/day") ? 1 : 0}
                                            >
                                                <Icon.CalendarV2 color="none" strokeColor="currentColor" size="20px" />
                                                <span>Day</span>
                                            </DrawerSubItem>
                                            <DrawerSubItem to="/scheduler/loadout"
                                                onClick={() => setVisible(false)}
                                                active={matchPath(pathname, "/scheduler/loadout") ? 1 : 0}
                                            >
                                                <Icon.Loadout color="none" strokeColor="currentColor" size="20px" />
                                                <span>Load Out</span>
                                            </DrawerSubItem>
                                        </div>
                                    </DrawerSection>
                                    <IsGranted feature={AccessCheck.features.INCIDENTS}>
                                        <DrawerSection>
                                            <div style={{ marginBottom: "16px", borderBottom: "1px solid #e2e8f0", paddingBottom: "12px" }}>
                                                <DrawerItem to="/incidents">Incidents</DrawerItem>
                                                <DrawerSubItem to="/incidents"
                                                    onClick={() => setVisible(false)}
                                                    active={matchPath(pathname, "/incidents") ? 1 : 0}
                                                >
                                                    <Icon.WarningV2 color="none" strokeColor="currentColor" size="20px" />
                                                    <span>Incidents</span>
                                                </DrawerSubItem>
                                            </div>
                                        </DrawerSection>
                                    </IsGranted>
                                    <DrawerSection>
                                        <div style={{ marginBottom: "16px", borderBottom: "1px solid #e2e8f0", paddingBottom: "12px" }}>
                                            <DrawerItem to="/chat">Communication</DrawerItem>
                                            <SecondaryAction
                                                onClick={() => {
                                                    setVisible(false);
                                                    history.push("/chat");
                                                }}
                                                active={matchPath(pathname, "/chat") ? 1 : 0}
                                            >
                                                <Icon.ChatV2 color="none" strokeColor="currentColor" size="20px" />
                                                <span>Chat</span>
                                            </SecondaryAction>
                                            <SecondaryAction onClick={() => {
                                                setVisible(false);
                                                history.push("/phonebook");
                                            }}
                                                active={matchPath(pathname, "/phonebook") ? 1 : 0}
                                            >
                                                <Icon.Persons color="currentColor" size="20px" />
                                                <span>Phonebook</span>
                                            </SecondaryAction>
                                        </div>
                                    </DrawerSection>
                                    <DrawerSection>
                                        <div style={{ marginBottom: "16px", paddingBottom: "12px" }}>
                                            <DrawerItem to="/edit-profile">Account</DrawerItem>
                                            <SecondaryAction onClick={() => {
                                                setVisible(false);
                                                history.push("/profile");
                                            }}
                                                active={matchPath(pathname, "/profile") ? 1 : 0}
                                            >
                                                <Icon.Person color="currentColor" size="20px" />
                                                <span>Profile</span>
                                            </SecondaryAction>
                                            <Spacer top={1} />
                                            <IsGranted feature={AccessCheck.features.SETTINGS}>
                                                <DisabledItem>
                                                    <Icon.Cog color="currentColor" size="20px" />
                                                    Settings
                                                    <ComingSoonBadge>Coming Soon</ComingSoonBadge>
                                                </DisabledItem>
                                            </IsGranted>
                                            {/* <DrawerSubItem to="/edit-profile" onClick={() => setVisible(false)}>
                                            <Icon.Person size="22px" style={{ marginRight: "10px" }} />
                                            Profile
                                        </DrawerSubItem>
                                        <IsGranted feature={AccessCheck.features.SETTINGS}>
                                            <DrawerSubItem to="/settings" onClick={() => setVisible(false)}>
                                                <Icon.Cog size="22px" style={{ marginRight: "10px" }} />
                                                Settings
                                            </DrawerSubItem>
                                        </IsGranted> */}
                                        </div>
                                    </DrawerSection>
                                </div>
                                <DrawerFooter>
                                    <StartInspection
                                        type="btn"
                                        title="Start Inspection"
                                        vehicleId={0}
                                        style={{
                                            minHeight: "48px",
                                            paddingBlock: "12px",
                                            borderRadius: "16px",
                                            gap: 8,
                                            fontSize: "16px",
                                        }}
                                        hideSubMenu={() => setVisible(false)}
                                        icon={
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <path d="M8 12h8"></path>
                                                <path d="M12 8v8"></path>
                                            </svg>
                                        }
                                    />
                                    <Spacer top={3} />
                                    <ReportButton onClick={handleReportBtnClick}>
                                        <Icon.Warning size="20px" color="currentColor" />
                                        <span>Report Issue</span>
                                    </ReportButton>
                                </DrawerFooter>
                            </DrawerWrapper>
                        </Modal>
                    )}
                </div>
            </SpacerInline>
        </MobileNav>
    );
};

export default React.memo(App);
